import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2 } from "@angular/core";

@Directive({
    selector: '[customTooltip]',
    standalone: true,
})
export class CustomTooltipDirective implements OnInit, AfterViewInit, OnDestroy {
    @Input() customTooltip: string = '';
    @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'right';
    @Input() tooltipDelay: number = 300;
    @Input() showOnlyWhenDisabled: boolean = false;
    @Input() showOnlyWhenTruncated: boolean = true; // Cambiado a true por defecto

    private tooltipElement: HTMLElement | null = null;
    private showTimeout: any;
    private static activeTooltip: HTMLElement | null = null;

    constructor(
        private readonly el: ElementRef,
        private readonly renderer: Renderer2
    ) { }

    ngOnInit() {
        this.removeTitleAttribute();
    }

    ngAfterViewInit() {
        setTimeout(() => this.removeTitleAttribute(), 0);
    }

    private removeTitleAttribute() {
        const element = this.el.nativeElement;
        this.renderer.removeAttribute(element, 'title');

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'title') {
                    this.renderer.removeAttribute(element, 'title');
                }
            });
        });

        observer.observe(element, {
            attributes: true,
            attributeFilter: ['title']
        });
    }

    @HostListener('mouseenter')
    onMouseEnter() {
        this.removeTitleAttribute();

        if (!this.customTooltip || this.customTooltip.trim() === '') {
            return;
        }

        if (this.showOnlyWhenDisabled) {
            const element = this.el.nativeElement as HTMLInputElement;
            if (!element.disabled) {
                return;
            }
        }

        if (this.showOnlyWhenTruncated && !this.isTextTruncated()) {
            return;
        }

        this.hideAllTooltips();

        this.showTimeout = setTimeout(() => {
            this.showTooltip();
        }, this.tooltipDelay);
    }

    @HostListener('mouseleave')
    onMouseLeave() {
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
        }
        this.hideTooltip();
    }

    private hideAllTooltips() {
        if (CustomTooltipDirective.activeTooltip) {
            this.renderer.removeChild(document.body, CustomTooltipDirective.activeTooltip);
            CustomTooltipDirective.activeTooltip = null;
        }
    }

    private isTextTruncated(): boolean {
        const element = this.el.nativeElement;
        
        // Para inputs o textareas, verificar el valor o placeholder
        if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
            const hasValue = element.value && element.value.trim() !== '';
            
            if (hasValue) {
                return element.scrollWidth > element.clientWidth;
            } else if (element.placeholder) {
                return this.isPlaceholderTruncated(element);
            }
            return false;
        }
        
        // Para otros elementos, verificar scrollWidth vs clientWidth con margen de 1px
        const diff = element.scrollWidth - element.clientWidth;
        return diff > 1;
    }

    private isPlaceholderTruncated(input: HTMLInputElement | HTMLTextAreaElement): boolean {
        if (!input.placeholder) {
            return false;
        }

        const tempSpan = this.renderer.createElement('span');
        this.renderer.setStyle(tempSpan, 'visibility', 'hidden');
        this.renderer.setStyle(tempSpan, 'position', 'absolute');
        this.renderer.setStyle(tempSpan, 'white-space', 'nowrap');
        
        const computedStyle = window.getComputedStyle(input);
        this.renderer.setStyle(tempSpan, 'font-family', computedStyle.fontFamily);
        this.renderer.setStyle(tempSpan, 'font-size', computedStyle.fontSize);
        this.renderer.setStyle(tempSpan, 'font-weight', computedStyle.fontWeight);
        this.renderer.setStyle(tempSpan, 'font-style', computedStyle.fontStyle);
        this.renderer.setStyle(tempSpan, 'letter-spacing', computedStyle.letterSpacing);
        
        this.renderer.setProperty(tempSpan, 'textContent', input.placeholder);
        this.renderer.appendChild(document.body, tempSpan);
        
        const placeholderWidth = tempSpan.offsetWidth;
        const inputWidth = input.clientWidth - parseInt(computedStyle.paddingLeft) - parseInt(computedStyle.paddingRight);
        
        this.renderer.removeChild(document.body, tempSpan);
        
        return placeholderWidth > inputWidth;
    }

    private showTooltip() {
        this.hideAllTooltips();

        this.tooltipElement = this.renderer.createElement('div');

        const tooltipContent = this.renderer.createElement('div');
        const text = this.renderer.createText(this.customTooltip);
        this.renderer.appendChild(tooltipContent, text);

        const arrow = this.renderer.createElement('div');

        this.renderer.setStyle(this.tooltipElement, 'position', 'fixed');
        this.renderer.setStyle(this.tooltipElement, 'z-index', '99999');
        this.renderer.setStyle(this.tooltipElement, 'pointer-events', 'none');

        this.renderer.setStyle(tooltipContent, 'background-color', '#1f2937');
        this.renderer.setStyle(tooltipContent, 'color', 'white');
        this.renderer.setStyle(tooltipContent, 'padding', '8px 12px');
        this.renderer.setStyle(tooltipContent, 'border-radius', '8px');
        this.renderer.setStyle(tooltipContent, 'font-size', '12px');
        this.renderer.setStyle(tooltipContent, 'font-weight', 'normal');
        this.renderer.setStyle(tooltipContent, 'line-height', '1.4');
        this.renderer.setStyle(tooltipContent, 'max-width', '300px');
        this.renderer.setStyle(tooltipContent, 'box-shadow', '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)');
        this.renderer.setStyle(tooltipContent, 'white-space', 'normal');
        this.renderer.setStyle(tooltipContent, 'word-wrap', 'break-word');

        this.renderer.setStyle(arrow, 'position', 'absolute');
        this.renderer.setStyle(arrow, 'width', '0');
        this.renderer.setStyle(arrow, 'height', '0');
        this.renderer.setStyle(arrow, 'border-style', 'solid');

        this.positionTooltip(tooltipContent, arrow);

        this.renderer.appendChild(this.tooltipElement, tooltipContent);
        this.renderer.appendChild(this.tooltipElement, arrow);
        this.renderer.appendChild(document.body, this.tooltipElement);

        CustomTooltipDirective.activeTooltip = this.tooltipElement;
    }

    private positionTooltip(content: HTMLElement, arrow: HTMLElement) {
        const hostPos = this.el.nativeElement.getBoundingClientRect();
        const tooltipPos = { top: 0, left: 0 };
        const gap = 12;

        this.renderer.setStyle(content, 'visibility', 'hidden');
        this.renderer.setStyle(content, 'position', 'fixed');
        this.renderer.appendChild(document.body, content);
        const contentWidth = content.offsetWidth;
        const contentHeight = content.offsetHeight;
        this.renderer.removeChild(document.body, content);
        this.renderer.removeStyle(content, 'visibility');
        this.renderer.removeStyle(content, 'position');

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const scrollX = window.scrollX || window.pageXOffset;
        const scrollY = window.scrollY || window.pageYOffset;

        let position = this.tooltipPosition;
        let finalPosition = position;

        switch (position) {
            case 'top':
                tooltipPos.left = hostPos.left + (hostPos.width / 2);
                tooltipPos.top = hostPos.top - contentHeight - gap;

                if (tooltipPos.top < scrollY) {
                    finalPosition = 'bottom';
                    tooltipPos.top = hostPos.bottom + gap;
                }
                if (tooltipPos.left - contentWidth / 2 < scrollX) {
                    tooltipPos.left = scrollX + contentWidth / 2 + 10;
                }
                if (tooltipPos.left + contentWidth / 2 > scrollX + viewportWidth) {
                    tooltipPos.left = scrollX + viewportWidth - contentWidth / 2 - 10;
                }
                break;

            case 'right':
                tooltipPos.top = hostPos.top + (hostPos.height / 2) - (contentHeight / 2);
                tooltipPos.left = hostPos.right + gap;

                if (tooltipPos.left + contentWidth > scrollX + viewportWidth) {
                    finalPosition = 'left';
                    tooltipPos.left = hostPos.left - contentWidth - gap;
                }
                if (tooltipPos.top < scrollY) {
                    tooltipPos.top = scrollY + 10;
                }
                if (tooltipPos.top + contentHeight > scrollY + viewportHeight) {
                    tooltipPos.top = scrollY + viewportHeight - contentHeight - 10;
                }
                break;

            case 'bottom':
                tooltipPos.left = hostPos.left + (hostPos.width / 2);
                tooltipPos.top = hostPos.bottom + gap;

                if (tooltipPos.top + contentHeight > scrollY + viewportHeight) {
                    finalPosition = 'top';
                    tooltipPos.top = hostPos.top - contentHeight - gap;
                }
                if (tooltipPos.left - contentWidth / 2 < scrollX) {
                    tooltipPos.left = scrollX + contentWidth / 2 + 10;
                }
                if (tooltipPos.left + contentWidth / 2 > scrollX + viewportWidth) {
                    tooltipPos.left = scrollX + viewportWidth - contentWidth / 2 - 10;
                }
                break;

            case 'left':
                tooltipPos.top = hostPos.top + (hostPos.height / 2) - (contentHeight / 2);
                tooltipPos.left = hostPos.left - contentWidth - gap;

                if (tooltipPos.left < scrollX) {
                    finalPosition = 'right';
                    tooltipPos.left = hostPos.right + gap;
                }
                if (tooltipPos.top < scrollY) {
                    tooltipPos.top = scrollY + 10;
                }
                if (tooltipPos.top + contentHeight > scrollY + viewportHeight) {
                    tooltipPos.top = scrollY + viewportHeight - contentHeight - 10;
                }
                break;
        }

        switch (finalPosition) {
            case 'top':
                this.renderer.setStyle(arrow, 'border-width', '6px 6px 0 6px');
                this.renderer.setStyle(arrow, 'border-color', '#1f2937 transparent transparent transparent');
                this.renderer.setStyle(arrow, 'bottom', '-6px');
                this.renderer.setStyle(arrow, 'left', '50%');
                this.renderer.setStyle(arrow, 'transform', 'translateX(-50%)');
                this.renderer.setStyle(content, 'transform', 'translateX(-50%)');
                break;

            case 'right':
                this.renderer.setStyle(arrow, 'border-width', '6px 6px 6px 0');
                this.renderer.setStyle(arrow, 'border-color', 'transparent #1f2937 transparent transparent');
                this.renderer.setStyle(arrow, 'left', '-6px');
                this.renderer.setStyle(arrow, 'top', '50%');
                this.renderer.setStyle(arrow, 'transform', 'translateY(-50%)');
                break;

            case 'bottom':
                this.renderer.setStyle(arrow, 'border-width', '0 6px 6px 6px');
                this.renderer.setStyle(arrow, 'border-color', 'transparent transparent #1f2937 transparent');
                this.renderer.setStyle(arrow, 'top', '-6px');
                this.renderer.setStyle(arrow, 'left', '50%');
                this.renderer.setStyle(arrow, 'transform', 'translateX(-50%)');
                this.renderer.setStyle(content, 'transform', 'translateX(-50%)');
                break;

            case 'left':
                this.renderer.setStyle(arrow, 'border-width', '6px 0 6px 6px');
                this.renderer.setStyle(arrow, 'border-color', 'transparent transparent transparent #1f2937');
                this.renderer.setStyle(arrow, 'right', '-6px');
                this.renderer.setStyle(arrow, 'top', '50%');
                this.renderer.setStyle(arrow, 'transform', 'translateY(-50%)');
                break;
        }

        this.renderer.setStyle(this.tooltipElement, 'top', `${tooltipPos.top}px`);
        this.renderer.setStyle(this.tooltipElement, 'left', `${tooltipPos.left}px`);
    }

    private hideTooltip() {
        if (this.tooltipElement) {
            this.renderer.removeChild(document.body, this.tooltipElement);
            if (CustomTooltipDirective.activeTooltip === this.tooltipElement) {
                CustomTooltipDirective.activeTooltip = null;
            }
            this.tooltipElement = null;
        }
    }

    ngOnDestroy() {
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
        }
        this.hideTooltip();
    }
}